/**
 * InvoiceViewDetails sub-components.
 *
 * Extracted from InvoiceViewDetails.tsx to keep each function within ESLint
 * line/complexity limits.
 */

import { formatDate, getCountryName, getStateName } from './invoice-view-helpers'
import { formatAmount } from '../../../types/billing'

import type { Address, Invoice } from '../../../types/billing'

// =============================================================================
// Company address block (split into helpers to reduce complexity)
// =============================================================================

function formatCityStateLine(address: Address): string {
  const parts: string[] = []
  if (address.city) { parts.push(address.city) }
  if (address.state) {
    parts.push((address.city ? ', ' : '') + getStateName(address.state))
  }
  if (address.postalCode) {
    const sep = (address.city ?? address.state) ? ' ' : ''
    parts.push(sep + address.postalCode)
  }
  return parts.join('')
}

interface CompanyAddressBlockProps {
  address: Address
}

export function CompanyAddressBlock({ address }: CompanyAddressBlockProps): JSX.Element {
  const hasCityStateLine = Boolean(address.city || address.state || address.postalCode)
  const cityStateText = formatCityStateLine(address)
  const countryText = address.country
    ? (hasCityStateLine ? ', ' : '') + getCountryName(address.country)
    : ''
  const hasFinalLine = hasCityStateLine || Boolean(address.country)
  return (
    <>
      {address.line1 && <div>{address.line1}</div>}
      {address.line2 && <div>{address.line2}</div>}
      {hasFinalLine && <div>{cityStateText}{countryText}</div>}
    </>
  )
}

// =============================================================================
// Seller contact icons / block
// =============================================================================

function EmailIcon(): JSX.Element {
  return (
    <svg className="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </svg>
  )
}

function PhoneIcon(): JSX.Element {
  return (
    <svg className="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </svg>
  )
}

function WebsiteIcon(): JSX.Element {
  return (
    <svg className="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </svg>
  )
}

export function SellerContact({ invoice }: { invoice: Invoice }): JSX.Element | null {
  if (!invoice.sellerContactEmail && !invoice.sellerContactPhone && !invoice.sellerWebsite) {
    return null
  }
  return (
    <div className="company-contact">
      {invoice.sellerContactEmail && (
        <div className="contact-item">
          <EmailIcon />
          <span>{invoice.sellerContactEmail}</span>
        </div>
      )}
      {invoice.sellerContactPhone && (
        <div className="contact-item">
          <PhoneIcon />
          <span>{invoice.sellerContactPhone}</span>
        </div>
      )}
      {invoice.sellerWebsite && (
        <div className="contact-item">
          <WebsiteIcon />
          <span>{invoice.sellerWebsite}</span>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Header Left
// =============================================================================

function CompanyIdentifiers({ gstin, pan, showPan }: { gstin?: string; pan?: string; showPan: boolean }): JSX.Element {
  return (
    <div className="company-identifiers">
      {gstin?.trim() && (
        <div className="identifier-item">
          <span className="identifier-label">GSTIN:</span>
          <span className="value">{gstin}</span>
        </div>
      )}
      {showPan && pan?.trim() && (
        <div className="identifier-item">
          <span className="identifier-label">PAN:</span>
          <span className="value">{pan}</span>
        </div>
      )}
    </div>
  )
}

interface HeaderLeftProps {
  invoice: Invoice
  isVendor: boolean
  sellerAddress: Address | null
  partyBillingAddress: Address | null
}

export function HeaderLeft({ invoice, isVendor, sellerAddress, partyBillingAddress }: HeaderLeftProps): JSX.Element {
  const displayAddress = isVendor ? partyBillingAddress : sellerAddress
  const gstin = isVendor ? invoice.partyGstin : invoice.sellerGstin
  const displayName = isVendor ? invoice.partyName : invoice.sellerName
  const hasLogo = !isVendor && Boolean(invoice.sellerLogoUrl)
  return (
    <div className="header-left">
      {hasLogo && (
        <div className="company-logo">
          <img alt={invoice.sellerName} src={invoice.sellerLogoUrl} />
        </div>
      )}
      <div className="company-details">
        <h2 className="company-name">{displayName}</h2>
        <div className="company-address">
          {displayAddress && <CompanyAddressBlock address={displayAddress} />}
        </div>
        <CompanyIdentifiers gstin={gstin} pan={invoice.sellerPan} showPan={!isVendor} />
        {!isVendor && <SellerContact invoice={invoice} />}
      </div>
    </div>
  )
}

// =============================================================================
// Header Right (split into smaller pieces)
// =============================================================================

function ProformaReference({ invoice }: { invoice: Invoice }): JSX.Element | null {
  const isShow = invoice.status === 'posted'
    && Boolean(invoice.proformaNumber)
    && invoice.proformaNumber !== invoice.invoiceNumber
  if (!isShow) { return null }
  return (
    <div className="proforma-reference">
      <span className="proforma-label">Proforma:</span> {invoice.proformaNumber}
    </div>
  )
}

function InvoiceDetailRows({ invoice }: { invoice: Invoice }): JSX.Element {
  return (
    <div className="invoice-details">
      <div className="detail-row">
        <span className="detail-label">Invoice Date</span>
        <span className="detail-value">{formatDate(invoice.invoiceDate)}</span>
      </div>
      {invoice.supplierBillNumber && (
        <div className="detail-row">
          <span className="detail-label">Supplier Bill #</span>
          <span className="detail-value">{invoice.supplierBillNumber}</span>
        </div>
      )}
      {invoice.dueDate && (
        <div className="detail-row">
          <span className="detail-label">Due Date</span>
          <span className="detail-value">{formatDate(invoice.dueDate)}</span>
        </div>
      )}
      {invoice.paymentTerms && (
        <div className="detail-row">
          <span className="detail-label">Payment Terms</span>
          <span className="detail-value">{invoice.paymentTerms}</span>
        </div>
      )}
      {invoice.poNumber && (
        <div className="detail-row">
          <span className="detail-label">Customer PO</span>
          <span className="detail-value">{invoice.poNumber}</span>
        </div>
      )}
    </div>
  )
}

function AmountDueBox({ invoice, baseCurrency }: { invoice: Invoice; baseCurrency: string }): JSX.Element {
  return (
    <div className="amount-due-box">
      <div className="amount-due-label">
        AMOUNT DUE ({invoice.currency || baseCurrency})
      </div>
      <div className="amount-due-value">
        {invoice.currency} {formatAmount(invoice.totalAmount, invoice.currency || baseCurrency)}
      </div>
    </div>
  )
}

interface HeaderRightProps {
  invoice: Invoice
  docHeading: string
  isExport: boolean
  baseCurrency: string
}

export function HeaderRight({ invoice, docHeading, isExport, baseCurrency }: HeaderRightProps): JSX.Element {
  const isShowInr = isExport && invoice.currency !== baseCurrency && Boolean(invoice.currency)
  return (
    <div className="header-right">
      <div className="doc-title"><h1>{docHeading}</h1></div>
      <div className="invoice-number"># {invoice.invoiceNumber}</div>
      <ProformaReference invoice={invoice} />
      <InvoiceDetailRows invoice={invoice} />
      <AmountDueBox baseCurrency={baseCurrency} invoice={invoice} />
      {isShowInr && (
        <div className="inr-equivalent">
          {baseCurrency} Equivalent: {baseCurrency}{' '}
          {formatAmount(invoice.baseTotalAmount, baseCurrency)}
        </div>
      )}
    </div>
  )
}
